export interface EventDateTimeDisplay {
  dateLabel: string;
  endDateLabel?: string;
  isMultiDay: boolean;
  timeLabel: string;
}

const longDateFormatter = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const timeFormatter = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});

export function formatEventDateTimeDisplay(startTimestamp: number, endTimestamp: number): EventDateTimeDisplay {
  const startDate = new Date(startTimestamp);
  const endDate = new Date(endTimestamp);
  const isMultiDay = !isSameLocalDate(startDate, endDate);

  return {
    dateLabel: longDateFormatter.format(startDate),
    ...(isMultiDay ? { endDateLabel: `bis ${longDateFormatter.format(endDate)}` } : {}),
    isMultiDay,
    timeLabel: `${timeFormatter.format(startDate)} - ${timeFormatter.format(endDate)} Uhr`,
  };
}

function isSameLocalDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const shortEndDayFormatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });

/** "bis So., 12. Okt." for events that end on a later day; null for single-day events. */
export function formatEventEndDayLabel(startTimestamp: number, endTimestamp: number): string | null {
  const startDate = new Date(startTimestamp);
  const endDate = new Date(endTimestamp);
  return isSameLocalDate(startDate, endDate) ? null : `bis ${shortEndDayFormatter.format(endDate)}`;
}

const SEARCH_DAY_MS = 24 * 60 * 60 * 1000;
const MAX_SEARCH_DAYS = 14;

/**
 * Textformen eines Termins fuer die Suche: "12.10.2026", "12. Oktober 2026" und "Freitag" fuer jeden Tag des
 * Events (hoechstens 14 Tage). Da die Suche Teilstrings vergleicht, treffen auch "12.10.", "Okt" oder "2026".
 * `timeZone` ist im Backend noetig, weil Convex in UTC rechnet.
 */
export function buildEventDateSearchValues(startTimestamp: number, endTimestamp: number, timeZone?: string): string[] {
  const numeric = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone });
  const long = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long', year: 'numeric', timeZone });
  const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long', timeZone });
  const values = new Set<string>();
  const lastTimestamp = Math.max(startTimestamp, endTimestamp);
  for (
    let timestamp = startTimestamp, day = 0;
    day < MAX_SEARCH_DAYS && (day === 0 || timestamp <= lastTimestamp);
    timestamp += SEARCH_DAY_MS, day += 1
  ) {
    const date = new Date(timestamp);
    values.add(numeric.format(date));
    values.add(long.format(date));
    values.add(weekday.format(date));
  }
  return [...values];
}
